import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getDevice,
  postNewDevice,
  getDeviceGeneral,
  getDeviceTelecareStatus,
  getDeviceTelecareStatusHistory,
  getDeviceStatusHistoryList,
  getDeviceStatusHistoryDetail,
  getDeviceRefreshStatus,
  getDeviceUpdateOs,
  getDeviceRequest,
  postDeviceNote,
  getDeviceNotes,
  deleteDeviceNotes,
  getHistoricDeviceUsers,
  getDeviceRequestSograce,
  searchDeviceEvents,
  searchDevicePeripheralEvents,
  putDeviceRemoteSupport
} from "services/api";
import { setCommandSentErrorCode, updateDeviceNotes } from "./deviceDetailSlice";
import { pushNotification } from "features/notifications/notificationsSlice";
import { i18n } from "utils";

export const getDeviceAsync: any | null = createAsyncThunk(
  'deviceDetail/getDevice',
  async (data: any) => {
    const response = await getDevice(data);
    return response;
  }
);

export const postNewDeviceAsync: any | null = createAsyncThunk(
  'deviceDetail/postNewDevice',
  async (data: any) => {
    const response = await postNewDevice(data);
    return response;
  }
);

export const getDeviceGeneralAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceGeneral',
  async (data: any, { getState }) => {
    const state: any = getState();
    const response = await getDeviceGeneral(data.id);
    const allServiceStates: any[] = state.masters?.serviceStates?.data ?? [];
    const serviceStateLookup: Record<string, string> = Object.fromEntries(
      allServiceStates.map((s: any) => [s.code, s.label ?? s.code])
    );
    return { response, serviceStateLookup };
  }
);

export const getDeviceStatusAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceTelecareStatus',
  async (data: any) => {
    const response = await getDeviceTelecareStatus(data);
    return response;
  }
);


export const postDeviceNoteAsync: any | null = createAsyncThunk(
  'deviceDetail/postDeviceNote',
  async (data: any, { dispatch }) => {
    const response = await postDeviceNote(data);
    response?.data.success && dispatch(getDeviceNotesAsync({
      id: data.referenceId,
      init: 0,
      size: 10,
    }));
    return response;
  },
);

export const updateDeviceRemoteSupportAsync: any | null = createAsyncThunk(
  'deviceDetail/updateDeviceRemoteSupport',
  async (data: any, { dispatch }) => {
    const response = await putDeviceRemoteSupport(data.id, { provider: data.provider, address: data.address });
    if (response?.data?.success) {
      dispatch(getDeviceAsync(data.id));
      dispatch(pushNotification({ message: i18n('device.detail.remoteSupport.saved'), severity: 'success' }));
    }
    return response;
  },
);

export const getHistoricDeviceUsersAsync: any | null = createAsyncThunk(
  'deviceDetail/getHistoricDeviceUsers',
  async (data: any) => {
    const response = await getHistoricDeviceUsers(data);
    return response;
  },
);

export const getDeviceNotesAsync: any | null = createAsyncThunk(
  'deviceDetail/getPatientNotes',
  async (data: any) => {
    const response = await getDeviceNotes(data);
    return response;
  },
);

export const deleteDeviceNotesAsync: any | null = createAsyncThunk(
  'deviceDetail/deleteDeviceNotes',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const notes = state.deviceDetail.notes.data;
    const response = await deleteDeviceNotes(data);
    response?.data.success && dispatch(updateDeviceNotes({ notes, id: data.noteId }));
    return response;
  },
);

export const getDeviceTelecareStatusHistoryAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceTelecareStatusHistory',
  async (data: any) => {
    const response = await getDeviceTelecareStatusHistory(data.id, { fromDate: data.fromDate, toDate: data.toDate});
    return response;
  }
);

export const getDeviceStatusHistoryListAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceStatusHistoryList',
  async (data: { id: any; page?: number; size?: number; fromDate?: number | null; toDate?: number | null; append?: boolean }) => {
    const params: any = { page: data.page ?? 0, size: data.size ?? 50 };
    if (data.fromDate) params.fromDate = data.fromDate;
    if (data.toDate) params.toDate = data.toDate;
    const response = await getDeviceStatusHistoryList(data.id, params);
    return { response, page: params.page, append: !!data.append };
  }
);

export const getDeviceStatusHistoryDetailAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceStatusHistoryDetail',
  async (data: { id: any; historicId: any }) => {
    const response = await getDeviceStatusHistoryDetail(data.id, data.historicId);
    return { response, historicId: data.historicId };
  }
);

// Actividad del dispositivo: ahora vía búsqueda con filtros (scope fijo al dispositivo).
// data llega como { deviceId, init, size, page, ...filtros }; page es de UI y se descarta.
export const searchDeviceEventsAsync: any | null = createAsyncThunk(
  'deviceDetail/searchDeviceEvents',
  async (data: any) => {
    const { deviceId, page, ...body } = data;
    const response = await searchDeviceEvents(deviceId, body);
    return response;
  }
);

export const searchDevicePeripheralEventsAsync: any | null = createAsyncThunk(
  'deviceDetail/searchDevicePeripheralEvents',
  async (data: any) => {
    const { deviceId, page, ...body } = data;
    const response = await searchDevicePeripheralEvents(deviceId, body);
    return response;
  }
);

export const getDeviceRefreshStatusAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceRefreshStatus',
  async (data: any) => {
    const response = await getDeviceRefreshStatus(data);
    return response;
  }
);

export const getDeviceUpdateOsAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceRefreshStatus',
  async (data: any) => {
    const response = await getDeviceUpdateOs(data);
    return response;
  }
);

export const getDeviceRequestAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceRequest',
  async (data: any) => {
    const response = await getDeviceRequest(data.id, { command: data.command });
    return response;
  }
);

export const getDeviceRequestSograceAsync: any | null = createAsyncThunk(
  'deviceDetail/getDeviceRequestSograce',
  async (data: any, { dispatch }) => {
    const response = await getDeviceRequestSograce(data.id, { command: data.command });
    dispatch(setCommandSentErrorCode(response?.data?.errorCode ?? 0));
    return response;
  }
);
